import { useCallback, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import LoginPage from './components/login-page.jsx';
import Sidebar from './components/layout/sidebar.jsx';
import { MaintenanceBanner } from './components/layout/maintenance-banner.jsx';
import { LicenseBanner } from './components/layout/license-banner.jsx';
import { ToastProvider, useToast } from './components/ui/toast.jsx';
import { PwaPage } from './components/pages/placeholder-pages.jsx';
import { DashboardPage } from './components/pages/dashboard-page.jsx';
import { MapPage } from './components/pages/map-page.jsx';
import { NodesPage } from './components/pages/nodes-page.jsx';
import { OssPage } from './components/pages/oss-page.jsx';
import { PersonnelPage } from './components/pages/personnel-page.jsx';
import { ChannelsPage } from './components/pages/channels-page.jsx';
import { UsersPage } from './components/pages/users-page.jsx';
import { SettingsPage } from './components/pages/settings-page.jsx';
import { LogsPage } from './components/pages/logs-page.jsx';
import { AuditPage } from './components/pages/audit-page.jsx';
import { CertificatesPage } from './components/pages/certificates-page.jsx';
import { PolicyPage } from './components/pages/policy-page.jsx';
import { NotificationsPage } from './components/pages/notifications-page.jsx';
import { CopDatasetsPage } from './components/pages/cop-datasets-page.jsx';
import { CopCotBridgePage } from './components/pages/cop-cot-bridge-page.jsx';
import { CopNffiBridgePage } from './components/pages/cop-nffi-bridge-page.jsx';
import { CopAdatp3BridgePage } from './components/pages/cop-adatp3-bridge-page.jsx';
import { CopOrdersPage } from './components/pages/cop-orders-page.jsx';
import { CopLocationsPage } from './components/pages/cop-locations-page.jsx';
import { CopIntelligencePage } from './components/pages/cop-intelligence-page.jsx';
import { PocSessionsPage } from './components/pages/poc-sessions-page.jsx';
import { PocRecordingsPage } from './components/pages/poc-recordings-page.jsx';
import { api } from './api/index.js';

/**
 * Session-expiry pattern — copied verbatim from
 * `manuals/COP ADMS session-expiry-autologout-prompt.md`.
 *
 * Reactive paradigm. NO heartbeat, NO polling. The contract is:
 *   1. Every API call goes through `api/index.js#request()`.
 *   2. On 401 from a protected endpoint, the wrapper calls
 *      `api.onSessionExpired(msg)`.
 *   3. App.jsx registers that callback once at mount → `setUser(null)`.
 *   4. When `user === null`, the SPA renders LoginPage instead of
 *      DashboardShell — operator returns to the login form.
 *
 * Route-change session probe (RouteWatcher below) covers the case
 * where the operator surfs between pages that DON'T fetch data on
 * mount (e.g. cached dashboard views) — every navigation triggers
 * `api.me()`, which 401s if the cookie is dead → wrapper notifies →
 * user falls to LoginPage. This is the missing piece that the
 * previous heartbeat-based attempts tried (and failed) to provide.
 */
function FullScreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas">
      <div className="flex flex-col items-center gap-3 text-mist">
        <span className="loader-spinner h-5 w-5 text-accent" />
        <span className="text-sm">Loading TMS ADMS…</span>
      </div>
    </div>
  );
}

/**
 * Probes /api/auth/me on every route change to detect a dead session
 * BEFORE the operator commits any destructive action. Mounted inside
 * DashboardShell, so it only runs when the user is authenticated —
 * a 401 from here therefore unambiguously means "session expired
 * since login" (never "bootstrap on fresh page load").
 *
 * Subtle wiring: /api/auth/me lives in `AUTH_PROBE_PATHS` inside
 * api/index.js, which means the wrapper SKIPS firing
 * `onSessionExpired` for that path automatically — because the
 * bootstrap probe (api.me() on initial mount, before user is set)
 * also hits /api/auth/me and a fresh-load 401 must NOT toast
 * "Сессия завершена" at the operator. Consequence: we fire the
 * callback OURSELVES here on a 401 catch. Pre-Round-71 the catch
 * was empty with a comment claiming "wrapper already notified" —
 * it doesn't, and that was the root cause of the long-standing
 * "UI doesn't auto-logout on session expiry" complaint.
 */
function RouteWatcher() {
  const location = useLocation();
  const inFlight = useRef(false);
  useEffect(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    api.me()
      .catch((err) => {
        // 401 here means the server-side session is dead. The
        // wrapper deliberately skipped its own notify path for
        // /api/auth/me (see comment above) — fire onSessionExpired
        // manually so the App drops user → null → LoginPage renders.
        // Any other status is a transient network glitch; do
        // nothing and let the next request settle it.
        if (err && err.status === 401 && typeof api.onSessionExpired === 'function') {
          try {
            api.onSessionExpired('Сессия завершена. Войдите снова.');
          } catch {
            /* never let the callback crash the watcher */
          }
        }
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [location.pathname]);
  return null;
}

function DashboardShell({ user, onUserUpdated, onSignOut, onSettingsChanged }) {
  return (
    <div className="flex min-h-0 flex-1 bg-canvas">
      <RouteWatcher />
      <Sidebar user={user} onSignOut={onSignOut} />
      <Routes>
        <Route path="/"               element={<DashboardPage currentUser={user} />} />
        <Route path="/map"            element={<MapPage />} />
        <Route path="/nodes"          element={<NodesPage currentUser={user} />} />
        <Route path="/oss"            element={<OssPage currentUser={user} />} />
        <Route path="/channels"       element={<ChannelsPage currentUser={user} />} />
        <Route path="/personnel"      element={<PersonnelPage currentUser={user} />} />
        <Route
          path="/settings"
          element={
            <SettingsPage
              currentUser={user}
              onUserUpdated={onUserUpdated}
              onSettingsChanged={onSettingsChanged}
            />
          }
        />
        <Route path="/users"          element={<UsersPage currentUser={user} />} />
        <Route path="/certificates"   element={<CertificatesPage currentUser={user} />} />
        <Route path="/policy"         element={<PolicyPage currentUser={user} />} />
        <Route path="/notifications"  element={<NotificationsPage currentUser={user} />} />
        <Route path="/logs"           element={<LogsPage currentUser={user} />} />
        <Route path="/audit"          element={<AuditPage currentUser={user} />} />
        <Route path="/cop-datasets"      element={<CopDatasetsPage      currentUser={user} />} />
        <Route path="/cop-cot-bridge"    element={<CopCotBridgePage     currentUser={user} />} />
        <Route path="/cop-nffi-bridge"   element={<CopNffiBridgePage    currentUser={user} />} />
        <Route path="/cop-adatp3-bridge" element={<CopAdatp3BridgePage  currentUser={user} />} />
        <Route path="/cop-orders"        element={<CopOrdersPage        currentUser={user} />} />
        <Route path="/cop-locations"     element={<CopLocationsPage     currentUser={user} />} />
        <Route path="/cop-intelligence"  element={<CopIntelligencePage  currentUser={user} />} />
        <Route path="/poc-sessions"      element={<PocSessionsPage      currentUser={user} />} />
        <Route path="/poc-recordings"    element={<PocRecordingsPage    currentUser={user} />} />
        <Route path="/pwa"            element={<PwaPage />} />
        {/* Legacy redirect from the old "Talking Groups" route. */}
        <Route path="/talking-groups" element={<Navigate to="/channels" replace />} />
        <Route path="*"               element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function AppInner() {
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [publicSettings, setPublicSettings] = useState({ site_name: 'TMS ADMS', maintenance_message: '' });
  const toast = useToast();

  const refreshPublicSettings = useCallback(async () => {
    try {
      const { settings } = await api.settings.public();
      setPublicSettings(settings);
    } catch {
      /* keep last-known value */
    }
  }, []);

  // Register the reactive session-expiry callback ONCE at mount.
  // Whenever ANY API call returns 401 from a protected endpoint,
  // notifyAuthExpired() inside api/index.js (or RouteWatcher's
  // explicit fire) will invoke this — we drop `user` to null
  // (→ LoginPage renders) and toast the cause.
  //
  // dismissAll() FIRST: action handlers (button clicks, form
  // submits) have their own .catch which fires an error toast
  // moments before the 401 lands here. Without the wipe, the
  // operator briefly sees two toasts stacked — "Save failed: 401"
  // + "Сессия завершена". The wipe ensures only the session
  // warning is left visible while the SPA swaps to LoginPage.
  useEffect(() => {
    api.onSessionExpired = (msg) => {
      setUser(null);
      if (toast) {
        try { toast.dismissAll?.(); } catch { /* noop */ }
        if (msg) {
          try { toast.warning(msg, 'Сессия завершена'); } catch { /* noop */ }
        }
      }
    };
    return () => { api.onSessionExpired = null; };
  }, [toast]);

  // Bootstrap: load me + public settings in parallel.
  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.me().then((res) => res?.user || null).catch(() => null),
      api.settings.public().then((res) => res?.settings).catch(() => null),
    ]).then(([u, s]) => {
      if (!mounted) return;
      setUser(u);
      if (s) setPublicSettings(s);
      setBootstrapping(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Reflect site_name into the document title for browser-tab labelling.
  useEffect(() => {
    if (publicSettings.site_name) {
      document.title = `${publicSettings.site_name} — Coordinator`;
    }
  }, [publicSettings.site_name]);

  async function handleSignOut() {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    setUser(null);
  }

  let body;
  if (bootstrapping) body = <FullScreenLoader />;
  else if (!user) body = <LoginPage onLogin={setUser} />;
  else
    body = (
      <DashboardShell
        user={user}
        onUserUpdated={setUser}
        onSignOut={handleSignOut}
        onSettingsChanged={refreshPublicSettings}
      />
    );

  return (
    <div className="flex h-dvh flex-col">
      <MaintenanceBanner message={publicSettings.maintenance_message} />
      {/* LicenseBanner polls /api/license/status which requires auth —
          only mount when the user is logged in. It self-hides on clean
          active. */}
      {user && <LicenseBanner />}
      <div className="flex min-h-0 flex-1">{body}</div>
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AppInner />
    </ToastProvider>
  );
}
